import { Button, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import lscache from "lscache";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { toast } from "react-toastify";
import { useUser } from "../../contexts/UserContext";
import { useDeleteHandler } from "../../hooks/useDeleteHandler";
import { useEditHandler } from "../../hooks/useEditHandler";
import { useVehicleFunctions } from "../../hooks/vehicles/useVehicleFunctions";
import VehicleService from "../../services/vehicle-service";
import IVehicle, { VehicleComponentProps } from "../../types/vehicle-type";
import VehicleForm from "./VehicleForm";
import BaseCard from "../Handlers/CardHandler";

const VehicleComponent: React.FC<VehicleComponentProps> = ({
  canDelete,
  vehicleType,
}) => {
  const initialFormData = {
    vehicleType: vehicleType,
    price: "",
    vehicleBody: "",
    make: "",
    model: "",
    year: "",
    currency: "EUR",
    title: "",
    description: "",
    address: "",
    city: "",
    country: "Finland",
    photos: [],
    availableFrom: dayjs(),
    datePosted: dayjs(),
  };

  const initialFormDataRef = useRef(initialFormData);
  const prevVehicleTypeRef = useRef(vehicleType);

  const { userId } = useUser();

  const { cars, rvs, isLoading, setVehicles, loadVehicles } =
    useVehicleFunctions();

  const { handleEditClick, editing, setEditing, showForm, setShowForm } =
    useEditHandler<IVehicle>(VehicleService);

  const { handleDelete } = useDeleteHandler<IVehicle>(VehicleService);

  useEffect(() => {
    if (editing && vehicleType !== prevVehicleTypeRef.current) {
      toast.warn("Tab changed while editing. Form data is cleared", {
        position: "bottom-right",
      });
      setEditing(false);
      setShowForm(false);
      setFormData(initialFormDataRef.current);
    }
    prevVehicleTypeRef.current = vehicleType;
  }, [editing, setEditing, setShowForm, vehicleType]);

  const [formData, setFormData] = useState<IVehicle>(initialFormData);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set(vehicleType + "-" + name, value, 30);
  };

  const handleEditVehicle = async (vehicleData: IVehicle) => {
    const data = await handleEditClick(vehicleData);
    if (data) {
      setFormData(data);
    }
  };

  const handleDeleteVehicle = async (vehicleData: IVehicle) => {
    const data = await handleDelete(vehicleData);
    if (data) {
      const updatedVehicles = vehicles.filter(
        (currentVehicle) => currentVehicle.id !== vehicleData.id
      );
      toast.info("Deleted vehicle!", { position: "bottom-right" });
      setVehicles(updatedVehicles);
      loadVehicles();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const vehicleContent: IVehicle = {
        id: formData.id,
        vehicleType: formData.vehicleType,
        price: formData.price,
        vehicleBody: formData.vehicleBody,
        make: formData.make,
        model: formData.model,
        year: formData.year,
        currency: formData.currency,
        address: formData.address,
        title: formData.title,
        description: formData.description,
        city: formData.city,
        country: formData.country,
        photos: formData.photos,
        creatorId: userId,
        availableFrom: formData.availableFrom,
        datePosted: formData.datePosted,
      };
      if (editing === true) {
        await VehicleService.updateVehicle(vehicleContent.id!, vehicleContent);
        toast.success("Vehicle updated successfully!", {
          position: "bottom-right",
        });
      } else {
        await VehicleService.listVehicle(vehicleContent);
        toast.success("Vehicle listed successfully!", {
          position: "bottom-right",
        });
      }
      setEditing(false);
      setFormData(initialFormData);
      Object.keys(initialFormData).forEach((key) => {
        const vehicleKey = vehicleType + "-" + key;
        lscache.set(
          vehicleKey,
          initialFormData[key as keyof typeof initialFormData]
        );
      });
      setShowForm(false);
      loadVehicles();
    } catch (error) {
      console.error("Error listing vehicle:", error);
      toast.error(
        "Error listing or updating vehicle, check the console for more information",
        {
          position: "bottom-right",
        }
      );
    }
  };

  const vehicles = vehicleType === "car" ? cars : rvs;

  return (
    <div className="container">
      {isLoading && <div>...Loading</div>}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <header>
            <Typography variant="h3">
              {vehicleType === "car" ? (
                <FormattedMessage id="available-cars" />
              ) : (
                <FormattedMessage id="available-rvs" />
              )}
            </Typography>
          </header>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            sx={{ mt: 2, mb: 2, width: { xs: "100%", sm: "90%" } }}
            variant="contained"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm === null && (
              <Typography variant="body1">
                {vehicleType === "car" ? (
                  <FormattedMessage id="list-car" />
                ) : (
                  <FormattedMessage id="list-rv" />
                )}
              </Typography>
            )}
            {showForm !== null && (
              <Typography variant="body1">
                {showForm ? (
                  <FormattedMessage id="hide" />
                ) : (
                  <FormattedMessage id="show" />
                )}
              </Typography>
            )}
          </Button>
        </Grid>
        <Grid item xs={12}>
          {showForm && (
            <VehicleForm
              editing={editing}
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
              handleChange={handleChange}
              vehicleType={vehicleType}
            />
          )}
        </Grid>
        {vehicles.map((vehicle: IVehicle) => {
          const cardDetailsTop = (
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  {vehicle.title}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </Typography>
              </Grid>
              <Grid item>
                {vehicle.price && (
                  <Typography variant="h6" color="textSecondary">
                    <FormattedNumber
                      value={parseFloat(vehicle.price)}
                      // eslint-disable-next-line react/style-prop-object
                      style="currency" // Formatted number styles directly clash with react element styles.
                      currency={vehicle.currency}
                    />{" "}
                    <FormattedMessage id="per-day" />
                  </Typography>
                )}
              </Grid>
            </Grid>
          );
          const cardDetails = (
            <>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="type" />:{" "}
                <FormattedMessage id={vehicle.vehicleBody} />
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="location" />: {vehicle.address}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {vehicle.city}, {vehicle.country}
              </Typography>
            </>
          );
          return (
            <Grid item xs={12} md={4} key={vehicle.id}>
              <BaseCard
                creatorId={vehicle.creatorId!}
                images={vehicle.photos?.map((photo) => photo.data)}
                title={vehicle.title}
                price={vehicle.price}
                currency={vehicle.currency}
                description={vehicle.description}
                location={`${vehicle.city}, ${vehicle.country}`}
                topDetails={cardDetailsTop}
                details={cardDetails}
                listingType="vehicle"
                availableFrom={vehicle.availableFrom}
                canDelete={canDelete || userId === vehicle.creatorId}
                canModify={userId === vehicle.creatorId}
                onDelete={() => handleDeleteVehicle(vehicle)}
                onEdit={() => handleEditVehicle(vehicle)}
              />
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default VehicleComponent;
